import { Task } from '../../types';
import { formatTaskTimeRange } from './dateUtils';
import {
    getFlexiblePrecision, getFlexibleRange, getTaskEnd, getTaskStartDate, isAllDayTask, isStartSchedule,
} from './scheduleUtils';

export type TaskLabelContext = 'day' | 'month' | 'year';

export interface TaskScheduleLabels {
    deadline: string;
    startsAt: string;
    startsIn: string;
    startsOn: string;
}

export const getTaskScheduleLabel = (
    task: Task,
    language: string,
    currentYear: number,
    context?: TaskLabelContext,
    labels: TaskScheduleLabels = {
        deadline: 'Deadline', startsAt: 'Starts at', startsIn: 'Starts in', startsOn: 'Starts',
    },
    now?: Date,
) => {
    const taskDate = getTaskStartDate(task);
    if (!taskDate) return '';
    const precision = getFlexiblePrecision(task);
    const range = precision ? getFlexibleRange(task) : null;
    if (task.schedule?.kind === 'deadline') {
        if (task.schedule.precision === 'hour') {
            const { start } = formatTaskTimeRange(taskDate, 0);
            return `${labels.deadline}: ${start}`;
        }
        if (task.schedule.precision === 'year') return `${labels.deadline}: ${taskDate.getFullYear()}`;

        const options: Intl.DateTimeFormatOptions = task.schedule.precision === 'month'
            ? taskDate.getFullYear() === currentYear ? { month: 'short' } : { month: 'short', year: 'numeric' }
            : { weekday: 'short', month: 'short', day: 'numeric' };
        return `${labels.deadline}: ${taskDate.toLocaleDateString(language, options)}`;
    }
    const taskSchedule = task.schedule;
    if (taskSchedule && isStartSchedule(taskSchedule)) {
        if (taskSchedule.precision === 'hour') {
            const { start } = formatTaskTimeRange(taskDate, 0);
            if (now && taskDate < now && taskDate.toDateString() !== now.toDateString()) {
                const date = taskDate.toLocaleDateString(language, { weekday: 'short', month: 'short', day: 'numeric' });
                return `${labels.startsOn} ${date}, ${start}`;
            }
            return `${labels.startsAt} ${start}`;
        }
        if (taskSchedule.precision === 'year') return `${labels.startsIn} ${taskDate.getFullYear()}`;

        const options: Intl.DateTimeFormatOptions = taskSchedule.precision === 'month'
            ? taskDate.getFullYear() === currentYear ? { month: 'long' } : { month: 'long', year: 'numeric' }
            : { weekday: 'short', month: 'short', day: 'numeric' };
        const label = taskSchedule.precision === 'month' ? labels.startsIn : labels.startsOn;
        return `${label} ${taskDate.toLocaleDateString(language, options)}`;
    }
    if (!precision || !range) {
        if (isAllDayTask(task)) {
            return taskDate.toLocaleDateString(language, { weekday: 'short', month: 'short', day: 'numeric' });
        }
        const duration = (getTaskEnd(task).getTime() - taskDate.getTime()) / 60_000;
        const { start, end } = formatTaskTimeRange(taskDate, duration);
        const time = `${start} - ${end}`;
        const taskEnd = getTaskEnd(task);
        const crossesDay = taskDate.toDateString() !== taskEnd.toDateString();
        if (context === 'day' && crossesDay) {
            const options: Intl.DateTimeFormatOptions = {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            };
            return `${taskDate.toLocaleDateString(language, options)} – ${taskEnd.toLocaleDateString(language, options)}`;
        }
        if (context === 'day') return time;
        if (context) {
            const options: Intl.DateTimeFormatOptions = context === 'month'
                ? { weekday: 'short', day: 'numeric' }
                : { weekday: 'short', month: 'short', day: 'numeric' };
            return `${taskDate.toLocaleDateString(language, options)} · ${time}`;
        }
        return time;
    }
    if (precision === 'year') {
        return range.start.getFullYear() === range.end.getFullYear()
            ? range.start.getFullYear().toString()
            : `${range.start.getFullYear()} – ${range.end.getFullYear()}`;
    }
    if (precision === 'month') {
        const options: Intl.DateTimeFormatOptions = range.start.getFullYear() === currentYear
            && range.end.getFullYear() === currentYear ? { month: 'short' } : { month: 'short', year: 'numeric' };
        const start = range.start.toLocaleDateString(language, options);
        const end = range.end.toLocaleDateString(language, options);
        return start === end ? start : `${start} – ${end}`;
    }
    const sameDay = range.start.getFullYear() === range.end.getFullYear()
        && range.start.getMonth() === range.end.getMonth()
        && range.start.getDate() === range.end.getDate();
    return sameDay
        ? range.start.toLocaleDateString(language, { weekday: 'short', month: 'short', day: 'numeric' })
        : `${range.start.toLocaleDateString(language, { month: 'short', day: 'numeric' })} – ${range.end.toLocaleDateString(language, { month: 'short', day: 'numeric' })}`;
};
